import {
  CodeSystemLookup,
  CodeSystemLookupResult,
  CodeSystemProperties,
  CodeSystemPropertyValue,
  ParametersParameter,
  ParametersResource
} from '@healthnz-prm/fhir-engine-renderer/types'
import { apiEndpointUrl, xApiKey } from '@/env'
import { formatFhirDatetime } from './utils'

// $lookup returns whichever value[x] matches the property's declared type
function partValue(
  part?: ParametersParameter
): CodeSystemPropertyValue | undefined {
  if (!part) return undefined

  // `!== undefined` rather than truthiness: `false` and `0` are legitimate values
  if (part.valueString !== undefined) return part.valueString
  if (part.valueCode !== undefined) return part.valueCode
  if (part.valueBoolean !== undefined) return part.valueBoolean
  if (part.valueInteger !== undefined) return part.valueInteger
  if (part.valueDecimal !== undefined) return part.valueDecimal
  if (part.valueDateTime !== undefined)
    return formatFhirDatetime(part.valueDateTime)

  return part.valueCoding?.display ?? part.valueCoding?.code
}

export function toLookupResult(
  data: ParametersResource
): CodeSystemLookupResult {
  // an unknown code comes back as an OperationOutcome, which has no properties to read
  if (data.resourceType !== 'Parameters') return { properties: {} }

  const properties = (data.parameter ?? [])
    .filter(({ name }) => name === 'property')
    .reduce<CodeSystemProperties>((acc, parameter) => {
      const code = parameter.part?.find(
        ({ name }) => name === 'code'
      )?.valueCode
      const value = partValue(
        parameter.part?.find(({ name }) => name === 'value')
      )

      if (!code || value === undefined) return acc

      // a repeated property code collects every value, in response order
      return { ...acc, [code]: [...(acc[code] ?? []), value] }
    }, {})

  return {
    display: data.parameter?.find(({ name }) => name === 'display')
      ?.valueString,
    properties
  }
}

interface LookupCodeSystemOptions {
  questionnaireId: string
  authToken: string
}

export function lookupCodeSystem(
  system: Parameters<CodeSystemLookup>[0],
  { code, properties }: Parameters<CodeSystemLookup>[1],
  { questionnaireId, authToken }: LookupCodeSystemOptions
): ReturnType<CodeSystemLookup> {
  const url = new URL(
    `${apiEndpointUrl}/v1/nzhts/${questionnaireId}/codesystem/lookup`
  )

  url.searchParams.set('system', system)
  url.searchParams.set('code', code)
  // append rather than set - $lookup takes one `property` param per requested property
  properties.forEach(property => url.searchParams.append('property', property))

  return fetch(url, {
    headers: {
      'x-api-key': xApiKey,
      authorization: `Bearer ${authToken}`
    }
  })
    .then(res => {
      // an error body parses as JSON just fine, and would otherwise surface as a
      // concept with no properties rather than a failure
      if (!res.ok) {
        throw new Error(`CodeSystem lookup failed: ${res.status}`)
      }
      return res.json()
    })
    .then(toLookupResult)
    .catch(error => {
      throw new Error(`Could not look up code: ${error.message}`)
    })
}
